const BANK_ICONS = {
  KBZ: 'https://cdn.gold549.com/build/assets/img/payments/kbz.png',
  KBZPAY: 'https://cdn.gold549.com/build/assets/img/payments/kpay.png',
  WAVEPAY: 'https://cdn.gold549.com/build/assets/img/payments/wavepay.png',
  AYA: 'https://space-tech.sgp1.cdn.digitaloceanspaces.com/hulk333/general/aya_pay.jpeg',
  AYAPAY: 'https://space-tech.sgp1.cdn.digitaloceanspaces.com/hulk333/general/aya_pay.jpeg',
  YOMA: 'https://space-tech.sgp1.cdn.digitaloceanspaces.com/hulk333/general/aya_pay.jpeg',
};

const WALLET_TO_BANK_CODE = {
  kbzpay: 'KBZPAY',
  wave: 'WAVEPAY',
};

export function mapPspBanks(banks = []) {
  return banks.map((bank) => ({
    id: bank.code,
    name: bank.name,
    link: BANK_ICONS[bank.code] || null,
  }));
}

export function mapMyanpayWalletProviders(providers = []) {
  return providers.map((wallet) => {
    const code = WALLET_TO_BANK_CODE[wallet.code] || wallet.code.toUpperCase();

    return {
      id: code,
      name: wallet.name,
      link: BANK_ICONS[code] || null,
      wallet_provider: wallet.code,
    };
  });
}

export function mapMyanpayDepositAccounts(accounts = []) {
  return accounts.map((account) => {
    const code = account.bank_code
      || WALLET_TO_BANK_CODE[account.wallet_provider]
      || String(account.wallet_provider || '').toUpperCase();
    const labelParts = [
      account.wallet_provider_name || code,
      account.account_name,
      account.number,
    ].filter(Boolean);
    const rowId = account.id != null
      ? `wallet-${account.id}`
      : `${account.wallet_provider}:${account.number}`;

    return {
      id: rowId,
      myanpay_wallet_id: account.id ?? null,
      name: labelParts.join(' · '),
      link: BANK_ICONS[code] || null,
      wallet_provider: account.wallet_provider,
      wallet_provider_name: account.wallet_provider_name,
      bank_code: code,
      account_name: account.account_name,
      number: account.number,
    };
  });
}

export function defaultDepositMode(settings = {}) {
  const autoOn = settings.auto_deposit || settings.auto_deposit_enabled;

  if (autoOn && !settings.manual_deposit) return 'auto';
  if (settings.manual_deposit && !autoOn) return 'manual';
  return autoOn ? 'auto' : 'manual';
}

export function isMyanpayDepositProvider(settings = {}) {
  return normalizeProvider(settings.deposit_provider) === 'myanpay';
}

export function defaultWithdrawMode(settings = {}) {
  if (settings.auto_withdraw && !settings.manual_withdraw) return 'auto';
  if (settings.manual_withdraw && !settings.auto_withdraw) return 'manual';
  return settings.auto_withdraw ? 'auto' : 'manual';
}

export function normalizeProvider(provider) {
  if (!provider || provider === 'psp') {
    return 'mcpay';
  }

  return provider;
}

export function isMyanpayPayment(payment = {}, provider = null) {
  const resolved = provider || payment.provider;

  return resolved === 'myanpay' || Boolean(payment.requires_digits);
}

export function isMcpayPayment(payment = {}, provider = null) {
  return !isMyanpayPayment(payment, provider);
}

function normalizeWalletKey(value = '') {
  return String(value).toLowerCase().replace(/[\s_-]/g, '');
}

export function isKpayOrWavePayment(account = {}) {
  const id = normalizeWalletKey(account.id);
  const name = normalizeWalletKey(account.name);
  const walletProvider = normalizeWalletKey(account.wallet_provider);

  return ['kbzpay', 'kbz', 'wavepay', 'wave'].some((key) => (
    id.includes(key)
    || name.includes(key)
    || walletProvider.includes(key)
  ));
}

export function findManualAccountForWallet(manualAccounts = [], walletKey = '') {
  const key = normalizeWalletKey(walletKey);
  const isKbz = key.includes('kbz') || key.includes('kpay');
  const isWave = key.includes('wave');

  return manualAccounts.find((account) => {
    const name = normalizeWalletKey(account.name);

    if (isKbz && (name.includes('kbz') || name.includes('kpay'))) {
      return true;
    }

    if (isWave && name.includes('wave')) {
      return true;
    }

    return false;
  }) || null;
}

export function enrichWalletAccounts(banks = [], manualAccounts = [], myanpayConfig = {}) {
  const assignOnDeposit = Boolean(myanpayConfig.assign_account_on_deposit);

  return banks.map((bank) => {
    // MyanPay auto deposits assign a random wallet on the server after submit.
    if (assignOnDeposit && bank.wallet_provider) {
      return bank;
    }

    const manualAccount = findManualAccountForWallet(
      manualAccounts,
      bank.wallet_provider || bank.id || bank.name,
    );

    if (manualAccount) {
      return {
        ...bank,
        account_name: manualAccount.account_name,
        number: manualAccount.number,
      };
    }

    if (selectedHasOwnAccountDetails(bank)) {
      return bank;
    }

    return bank;
  });
}

function selectedHasOwnAccountDetails(bank = {}) {
  return Boolean(bank.account_name && bank.number);
}

function addUniqueBank(banks, seen, bank) {
  if (!bank?.id || seen.has(bank.id)) {
    return;
  }

  seen.add(bank.id);
  banks.push(bank);
}

export function resolveAutoDepositAccounts(config = {}) {
  const settings = config.payment_settings || {};
  const providers = config.providers || {};
  const provider = normalizeProvider(settings.deposit_provider);

  if (provider === 'myanpay' && providers.myanpay?.configured) {
    return mapMyanpayWalletProviders(providers.myanpay.wallet_providers || []);
  }

  if (provider === 'mcpay' && providers.mcpay?.configured) {
    return mapPspBanks(providers.mcpay.banks || []);
  }

  return [];
}

export function resolveAutoWithdrawAccounts(config = {}) {
  const settings = config.payment_settings || {};
  const providers = config.providers || {};
  const provider = normalizeProvider(settings.withdraw_provider || settings.deposit_provider);

  if (provider === 'myanpay' && providers.myanpay?.configured) {
    return mapMyanpayWalletProviders(providers.myanpay.wallet_providers || []);
  }

  if (provider === 'mcpay' && providers.mcpay?.configured) {
    return mapPspBanks(providers.mcpay.banks || []);
  }

  return [];
}

export function applyAutoDepositResponse(data = {}) {
  if (!data.payment) {
    return null;
  }

  return {
    depositId: data.data?.id ?? data.id ?? null,
    payment: data.payment,
    provider: normalizeProvider(data.provider || data.payment?.provider),
    resumed: Boolean(data.resumed),
    fallbackUsed: Boolean(data.fallback_used),
  };
}

/** @deprecated use applyAutoDepositResponse */
export function applyPspDepositResponse(data = {}) {
  return applyAutoDepositResponse(data);
}

export function applyAutoWithdrawResponse(data = {}) {
  if (data.summary) {
    const provider = normalizeProvider(data.provider || data.summary?.provider);

    return {
      withdrawId: data.id ?? data.summary.withdraw_id ?? null,
      summary: {
        ...data.summary,
        provider,
      },
      provider,
      gatewayResponse: data.gateway_response || null,
      resumed: Boolean(data.resumed),
      fallbackUsed: Boolean(data.fallback_used),
    };
  }

  const withdraw = data.data ?? null;
  const gateway = data.gateway_response || data.psp || data.myanpay || {};

  if (!withdraw?.id) {
    return null;
  }

  const bankCode = withdraw.psp_bank_code || withdraw.type || gateway.bank_code || '';
  const provider = normalizeProvider(
    data.provider || (withdraw.provider === 'myanpay' ? 'myanpay' : 'mcpay'),
  );

  return {
    withdrawId: withdraw.id,
    provider,
    gatewayResponse: gateway,
    summary: {
      withdraw_id: withdraw.id,
      provider,
      transaction_code: gateway.transaction_code || withdraw.psp_transaction_code,
      transaction_no: withdraw.psp_transaction_no || gateway.transaction_id || null,
      request_no: gateway.request_no || withdraw.psp_request_no,
      amount: withdraw.amount,
      bank_code: bankCode,
      bank_name: gateway.bank_name || withdraw.remark || bankCode,
      account_number: withdraw.account_number,
      account_name: withdraw.account_name,
      status: withdraw.status,
      otp_required: gateway.status === 'otp_required' || Boolean(gateway.tx_id),
      tx_id: gateway.tx_id || null,
      sent_to: gateway.sent_to || null,
    },
    resumed: Boolean(data.resumed),
    fallbackUsed: Boolean(data.fallback_used),
  };
}

/** @deprecated use applyAutoWithdrawResponse */
export function applyPspWithdrawResponse(data = {}) {
  return applyAutoWithdrawResponse(data);
}

export function applyPendingDepositFromConfig(pending = {}) {
  if (!pending?.id) {
    return null;
  }

  if (pending.payment) {
    return applyAutoDepositResponse({
      id: pending.id,
      provider: pending.provider,
      payment: pending.payment,
      resumed: pending.resumed,
    });
  }

  return null;
}

export function applyPendingWithdrawFromConfig(pending = {}) {
  if (!pending?.id) {
    return null;
  }

  if (pending.summary) {
    return applyAutoWithdrawResponse({
      id: pending.id,
      provider: pending.provider,
      summary: pending.summary,
      resumed: pending.resumed,
    });
  }

  return null;
}

export const MANUAL_WITHDRAW_ACCOUNTS = [
  {
    name: 'KBZ pay',
    id: 'KBZ pay',
    link: BANK_ICONS.KBZPAY,
  },
  {
    name: 'Wave pay',
    id: 'Wave pay',
    link: BANK_ICONS.WAVEPAY,
  },
];
